import { supabase } from '../lib/supabase';

/**
 * Wellness Services API
 * Handles fetching wellness services from the database
 */
export const wellnessService = {
  /**
   * Get all active wellness services
   * @param {string|null} category - Optional category filter
   * @returns {Promise<Array>} Array of wellness services
   */
  async getWellnessServices(category = null) {
    console.log('🔍 getWellnessServices called with category:', category);

    let query = supabase
      ?.from('wellness_services')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('category')
      ?.order('name');

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Error fetching wellness services:', error);
      throw error;
    }

    console.log(`✅ Fetched ${data?.length || 0} wellness services`);
    return data || [];
  },

  /**
   * Get wellness services grouped by category
   * @returns {Promise<Object>} Object with categories as keys and service arrays as values
   */
  async getWellnessServicesByCategory() {
    console.log('🔍 getWellnessServicesByCategory called');

    const services = await this.getWellnessServices();

    // Group services by category
    const grouped = services.reduce((acc, service) => {
      const category = service.category || 'other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(service);
      return acc;
    }, {});

    console.log('✅ Services grouped by category:', Object.keys(grouped));
    return grouped;
  },

  /**
   * Get a single wellness service by slug
   * @param {string} slug - Service slug
   * @returns {Promise<Object|null>} Service object or null
   */
  async getWellnessServiceBySlug(slug) {
    console.log('🔍 getWellnessServiceBySlug called with slug:', slug);

    const { data, error } = await supabase
      ?.from('wellness_services')
      ?.select('*')
      ?.eq('slug', slug)
      ?.eq('is_active', true)
      ?.single();

    if (error) {
      console.error('❌ Error fetching wellness service by slug:', error);
      throw error;
    }

    console.log('✅ Fetched service:', data?.name);
    return data;
  },

  /**
   * Get popular wellness services
   * @param {number} limit - Maximum number of services to return
   * @returns {Promise<Array>} Array of popular wellness services
   */
  async getPopularWellnessServices(limit = 6) {
    console.log('🔍 getPopularWellnessServices called with limit:', limit);

    const { data, error } = await supabase
      ?.from('wellness_services')
      ?.select('*')
      ?.eq('is_active', true)
      ?.eq('is_popular', true)
      ?.order('name')
      ?.limit(limit);

    if (error) {
      console.error('❌ Error fetching popular wellness services:', error);
      throw error;
    }

    console.log(`✅ Fetched ${data?.length || 0} popular services`);
    return data || [];
  },

  /**
   * Get wellness services by IDs
   * @param {Array<string>} serviceIds - Array of service IDs
   * @returns {Promise<Array>} Array of wellness services
   */
  async getWellnessServicesByIds(serviceIds) {
    console.log('🔍 getWellnessServicesByIds called with IDs:', serviceIds);

    if (!serviceIds || serviceIds.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      ?.from('wellness_services')
      ?.select('*')
      ?.in('id', serviceIds)
      ?.eq('is_active', true);

    if (error) {
      console.error('❌ Error fetching wellness services by IDs:', error);
      throw error;
    }

    console.log(`✅ Fetched ${data?.length || 0} services by IDs`);
    return data || [];
  },

  /**
   * Calculate total duration for multiple services
   * @param {Array<Object>} services - Array of service objects
   * @returns {number} Total duration in minutes
   */
  calculateTotalDuration(services) {
    if (!services || services.length === 0) {
      return 0;
    }

    return services.reduce((total, service) => {
      return total + (service.duration_minutes || 0);
    }, 0);
  },

  /**
   * Calculate total therapists required for multiple services
   * @param {Array<Object>} services - Array of service objects
   * @returns {number} Maximum therapists required
   */
  calculateTherapistsRequired(services) {
    if (!services || services.length === 0) {
      return 1;
    }

    // Return the maximum therapists_required from all services
    return Math.max(...services.map(service => service.therapists_required || 1));
  }
};

